import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Skeleton,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { ActionLogCreate, ActionLogEntry } from '../../types/analytics';

interface ActionLogProps {
  actionLog: ActionLogEntry[];
  actionLogLoading: boolean;
  createActionLogEntry: (entry: ActionLogCreate) => Promise<void>;
  deleteActionLogEntry: (id: string) => Promise<void>;
}

const ActionLog: React.FC<ActionLogProps> = ({
  actionLog,
  actionLogLoading,
  createActionLogEntry,
  deleteActionLogEntry,
}) => {
  const [actionDate, setActionDate] = useState('');
  const [actionDesc, setActionDesc] = useState('');
  const [actionType, setActionType] = useState<'change' | 'note'>('change');
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const handleAddAction = async () => {
    if (!actionDate || !actionDesc.trim()) return;
    setActionSubmitting(true);
    try {
      await createActionLogEntry({
        action_date: actionDate,
        description: actionDesc.trim(),
        entry_type: actionType,
      });
      setActionDate('');
      setActionDesc('');
    } catch (err) {
      console.error('Failed to create action log entry:', err);
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleDeleteAction = async (id: string) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await deleteActionLogEntry(id);
    } catch (err) {
      console.error('Failed to delete action log entry:', err);
    }
  };

  return (
    <Paper sx={{ p: 2.5, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Action Log
      </Typography>
      {/* Type toggle + form */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
        <Chip
          label="Change"
          size="small"
          variant={actionType === 'change' ? 'filled' : 'outlined'}
          color={actionType === 'change' ? 'primary' : 'default'}
          onClick={() => setActionType('change')}
          sx={{ fontSize: 11 }}
        />
        <Chip
          label="Note"
          size="small"
          variant={actionType === 'note' ? 'filled' : 'outlined'}
          color={actionType === 'note' ? 'primary' : 'default'}
          onClick={() => setActionType('note')}
          sx={{ fontSize: 11 }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField
          type="date"
          size="small"
          value={actionDate}
          onChange={(e) => setActionDate(e.target.value)}
          sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          placeholder={
            actionType === 'change'
              ? 'What changed on the website...'
              : 'Decision or context for insights...'
          }
          value={actionDesc}
          onChange={(e) => setActionDesc(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddAction();
          }}
          fullWidth
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
        />
        <IconButton
          color="primary"
          onClick={handleAddAction}
          disabled={actionSubmitting || !actionDate || !actionDesc.trim()}
          size="small"
        >
          <AddIcon />
        </IconButton>
      </Box>
      {/* Entries list */}
      <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 280 }}>
        {actionLogLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={40} />
            ))}
          </Box>
        ) : actionLog.length > 0 ? (
          <List dense disablePadding>
            {actionLog.map((entry) => (
              <ListItem key={entry.id} disableGutters sx={{ py: 0.5 }}>
                <Chip
                  label={entry.action_date}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 0.5, fontSize: 10, minWidth: 90 }}
                />
                <Chip
                  label={entry.entry_type === 'note' ? 'note' : 'change'}
                  size="small"
                  color={entry.entry_type === 'note' ? 'info' : 'default'}
                  sx={{ mr: 1, fontSize: 9, height: 18 }}
                />
                <ListItemText
                  primary={entry.description}
                  primaryTypographyProps={{ variant: 'body2', fontSize: 12 }}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" size="small" onClick={() => handleDeleteAction(entry.id)}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No actions logged yet. Record website changes to track their impact.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default ActionLog;
